import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "api";
import { BusinessInfo, ClientDetails, PaymentMethods, SelectedBank, Summary } from "dtos/application.dto";
import { BankSchemaDto } from "dtos/bank.dto";
import { LawSuitSchemaDto, LawSuitTypes, UpdateLawSuitDto } from "dtos/lawsuit.dto";
import { LoanSchemaDto } from "dtos/loan.dto";
import { T_Api_Success_Res } from "./api.types";
import { number } from "zod";

export const upstreamService = {
  useAddLoan: () => {
    return useMutation({
      mutationKey: ["upstream/add-loan"],
      mutationFn: async ({ appkey, loan }: { appkey: string; loan: LoanSchemaDto }) => {
        const { _id, ...rest } = loan;
        return await api.post(`/upstream/add/loan/${appkey}`, rest);
      },
    });
  },

  useAddLoanByFile: () => {
    return useMutation({
      mutationKey: ["upstream/add-loan-file"],
      mutationFn: async ({ appkey, files }: { appkey: string; files: FormData }) => {
        return api.post(`/upstream/add/loan/${appkey}?mode=file`, files, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      },
    });
  },

  useEditLoan: () => {
    return useMutation({
      mutationKey: ["upstream/edit-loan"],
      mutationFn: async ({
        appkey,
        loan,
        source,
        isFieldsChanged,
      }: {
        appkey: string;
        loan: Partial<LoanSchemaDto>;
        source: "create-application" | "application-page";
        isFieldsChanged?: boolean;
      }) => {
        let url = `/upstream/edit/loan/${appkey}`;
        if (source === "application-page" && isFieldsChanged) {
          url = `/upstream/edit/loan/${appkey}?resetStrategy=true`;
        }
        const res = await api.patch(url, loan);
        return res.data as T_Api_Success_Res<{ editedLoan: LoanSchemaDto }>;
      },
    });
  },

  useAddBank: () => {
    return useMutation({
      mutationKey: ["upstream/add-bank"],
      mutationFn: async ({ appkey, bank }: { appkey: string; bank: BankSchemaDto }) => {
        const { _id, ...rest } = bank;
        return await api.post(`/upstream/add/bank/${appkey}`, rest);
      },
    });
  },

  useAddBankByFile: () => {
    return useMutation({
      mutationKey: ["upstream/add-bank-file"],
      mutationFn: async ({ appkey, files }: { appkey: string; files: FormData }) => {
        return api.post(`/upstream/add/bank/${appkey}?mode=file`, files, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      },
    });
  },

  useEditBank: () => {
    return useMutation({
      mutationKey: ["upstream/edit-bank"],
      mutationFn: async ({ appkey, bank }: { appkey: string; bank: BankSchemaDto }) => {
        return await api.patch(`/upstream/edit/bank/${appkey}`, bank);
      },
    });
  },

  useCreateNewApplication: () => {
    return useMutation({
      mutationKey: ["upstream/create-application"],
      mutationFn: async ({ formData }: { formData: FormData }) => {
        const res = await api.post(`/upstream/create-application`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return res.data as T_Api_Success_Res<{
          appkey: string;
        }>;
      },
    });
  },

  useAddLawsuitByFile: () => {
    return useMutation({
      mutationKey: ["upstream/add-lawsuit-file"],
      mutationFn: async ({ appkey, lawsuits }: { appkey: string; lawsuits: FormData }) => {
        return api.post(`/upstream/add/lawsuit/${appkey}?mode=file`, lawsuits, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      },
    });
  },

  useAddAgreementFiles: () => {
    return useMutation({
      mutationKey: ["upstream/add-agreements"],
      mutationFn: async ({ appkey, files }: { appkey: string; files: FormData }) => {
        return await api.post(`/upstream/add/agreement/${appkey}`, files, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      },
    });
  },

  // useAddLawsuit: () => {
  //   return useMutation({
  //     mutationKey: ["upstream/add-lawsuit"],
  //     mutationFn: async ({ appkey, lawsuit }: { appkey: string; lawsuit: { lawsuit_insurance: boolean } }) => {
  //       return await api.post(`/upstream/add/lawsuit/${appkey}`, lawsuit);
  //     },
  //   });
  // },

  useEditLawsuit: () => {
    return useMutation({
      mutationKey: ["upstream/edit-lawsuit"],
      mutationFn: async ({ appkey, lawsuit }: { appkey: string; lawsuit: UpdateLawSuitDto }) => {
        return await api.patch(`/upstream/edit/lawsuit/${appkey}`, lawsuit);
      },
    });
  },

  useEditMetaInfo: () => {
    return useMutation({
      mutationKey: ["upstream/edit-meta-info"],
      mutationFn: async ({
        appkey,
        business_info,
        client_details,
        selected_bank,
        summary,
      }: {
        appkey: string;
        business_info?: Partial<BusinessInfo>;
        client_details?: Partial<ClientDetails>;
        selected_bank?: Partial<SelectedBank>;
        summary?: Partial<Summary>;
      }) => {
        return await api.patch(`/upstream/edit/meta-info/${appkey}`, {
          business_info,
          client_details,
          selected_bank,
          summary,
        });
      },
    });
  },
  useValidateMail: () => {
    return useMutation({
      mutationKey: ["validate/upstream-email"],
      mutationFn: async ({ email }: { email: string }) => {
        const res = await api.post(`/upstream/validate-email`, { email });
        return res.data as T_Api_Success_Res<{ userExists: boolean; lastAppkey: string }>;
      },
    });
  },
  useValidatePhone: () => {
    return useMutation({
      mutationKey: ["validate/upstream-phone"],
      mutationFn: async ({ phone }: { phone: string }) => {
        const res = await api.post(`/upstream/validate-phone`, { phone });
        return res.data as T_Api_Success_Res<{ userExists: boolean; lastAppkey: string }>;
      },
    });
  },
  useDeleteLoans: () => {
    return useMutation({
      mutationKey: ["delete/upstream-loan"],
      mutationFn: async ({ appkey, loan_id }: { appkey: string; loan_id: string }) => {
        const res = await api.post(`/upstream/delete-loan`, { appkey, loan_id });
        return res.data;
      },
    });
  },

  useSyncBalance: () => {
    return useMutation({
      mutationKey: ["sync/balance"],
      mutationFn: async ({ appkey }: { appkey: string }) => {
        const res = await api.post(`/upstream/sync-balance`, { appkey });
        return res.data;
      },
    });
  },

  useStartPaymentDetailsCapture: () => {
    return useMutation({
      mutationKey: ["upstream/collect/payment-info"],
      mutationFn: async ({ appkey, mode }: { appkey: string; mode: PaymentMethods }) => {
        const res = await api.post(`/upstream/collect-payment-info`, { appkey, mode });
        return res.data;
      },
    });
  },

  useSyncInfo: () => {
    return useMutation({
      mutationKey: ["sync-info"],
      mutationFn: async ({ appKey }: { appKey: string | undefined }) => {
        const res = await api.post(`/upstream/sync-loan-info`, { appkey: appKey });
        return res.data as T_Api_Success_Res<{ stopLoading: boolean }>;
      },
    });
  },

  useInitiatePandaDoc: () => {
    return useMutation({
      mutationKey: ["upstream/initiate-pandadoc"],
      mutationFn: async ({ appkey }: { appkey: string | undefined }) => {
        const res = await api.post(`/upstream/initiate-pandadoc`, { appkey });
        return res.data as T_Api_Success_Res<object>;
      },
    });
  },
  useSyncFinancialSummary: () => {
    return useMutation({
      mutationKey: ["sync-info"],
      mutationFn: async ({ appKey }: { appKey: string | undefined }) => {
        const res = await api.post(`/upstream/sync-financial-summary/${appKey}`);
        return res.data;
      },
    });
  },

  useChangeOrganisation: () => {
    return useMutation({
      mutationKey: ["chnage-org"],
      mutationFn: async ({ appkey, org_id }: { appkey: string | undefined; org_id: string }) => {
        const res = await api.post(`/upstream/change-application-org`, { appkey, org_id });
        return res.data;
      },
    });
  },

  useResetPandaDoc: () => {
    return useMutation({
      mutationKey: ["reset-pandadoc"],
      mutationFn: async ({ appkey }: { appkey: string }) => {
        const res = await api.post(`/upstream/reset-pandadoc`, { appkey });
        return res.data;
      },
    });
  },

  useGetLoanFilesPreview: (appkey: string | undefined) => {
    return useQuery({
      queryKey: ["upstream/loan-file-preview", appkey],
      queryFn: async () => {
        const res = await api.get(`/upstream/loans/files/${appkey}`);
        return res.data as T_Api_Success_Res<
          {
            loan_id: string;
            file_id: string;
            name: string;
            mime_type: string;
            preview_url: string;
            creditor_name: string;
          }[]
        >;
      },
      enabled: !!appkey,
    });
  },

  useRelinkPlaid: () => {
    return useMutation({
      mutationKey: ["relink-plaid"],
      mutationFn: async ({ appkey }: { appkey: string }) => {
        const res = await api.post(`/upstream/plaid/re-link`, { appkey });
        return res.data;
      },
    });
  },

  useRenameBankFiles: () => {
    return useMutation({
      mutationKey: ["rename-bank-files"],
      mutationFn: async ({ appkey, files }: { appkey: string; files: { _id: string; alias: string }[] }) => {
        const res = await api.post(`/upstream/rename-bank-files`, { appkey, files });
        return res.data;
      },
    });
  },

  useGetPandaDocPDF: () => {
    return useMutation({
      mutationKey: ["upstream/pd-file"],
      mutationFn: async ({ appkey, doc_id }: { appkey: string; doc_id: string }) => {
        const res = await api.post(`/upstream/pd-file`, { appkey, doc_id }, { responseType: "blob" });

        return res.data;
      },
    });
  },

  useGetMissingBankPeriods: (appkey: string | undefined) => {
    return useQuery({
      queryKey: ["upstream/missing-bank-periods", appkey],
      queryFn: async () => {
        const res = await api.get(`/upstream/missing-bank-periods/${appkey}`);
        return res.data as T_Api_Success_Res<{
          missingDates: string[];
          message: string | null;
          missingLoanDates: boolean;
        }>;
      },
      enabled: !!appkey,
    });
  },

  useDeleteBankFiles: () => {
    return useMutation({
      mutationKey: ["delete-bank-files"],
      mutationFn: async ({ appkey, files }: { appkey: string; files: string[] }) => {
        const res = await api.post(`/upstream/delete-bank-files`, { appkey, files });
        return res.data as T_Api_Success_Res<{
          deleted?: string[];
          failed?: { src: string; reason: string }[];
        }>;
      },
    });
  },

  useDeleteLawsuitFile: () => {
    return useMutation({
      mutationKey: ["upstream/delete-lawsuit"],
      mutationFn: async ({ appkey, lawsuit_id, file_id }: { appkey: string; lawsuit_id: string; file_id: string }) => {
        const res = await api.post(`/upstream/delete-lawsuit`, { appkey, lawsuit_id, file_id });
        return res.data as T_Api_Success_Res<{
          lawsuit_deleted: boolean;
          remaining_attachments: number;
          total_lawsuits: number;
        }>;
      },
    });
  },
};
